import {
  type LocalMonth,
  type Note,
  type Patient,
  type ReportEntry,
  type ReportPatientGroup,
  type ReportResponse,
  type RetrocessionMonth,
  type SendNoteResponse,
  formatEmails,
  parseEmails,
} from '@ergo/shared';
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api';
import { STATUS_LABELS, doneLabel } from '../appointments';
import { Toast, hearts, petalsOnce, useToast } from '../celebrations';
import {
  type CloseOutcome,
  type CloseProgress,
  type CloseStep,
  type Cloture,
  type PatientCloture,
  type VerificationRow,
  blockingReason,
  bouclageToast,
  closeHint,
  failureLines,
  firstNameOf,
  mergeOutcomes,
  outcomeSummary,
  planCloture,
  retryPlan,
  runClose,
  verificationOf,
} from '../cloture';
import { Declaration } from '../components/Declaration';
import { MoisEnCours } from '../components/MoisEnCours';
import { OutcomeButtons } from '../components/OutcomeButtons';
import { useResource } from '../hooks';
import { useEmailConfigured } from '../maps';
import { formatEuros, formatPercent } from '../money';
import { storageKey } from '../storage';
import {
  addMonths,
  dateOf,
  deMois,
  firstMonthOfQuarter,
  formatDate,
  formatMonthName,
  formatDuration,
  formatMonth,
  formatMonthLong,
  formatShortDate,
  isValidMonth,
  previousMonth,
  today,
  previousQuarter,
  quarterOf,
  timeOf,
} from '../time';
import { useRefreshNavCounts } from '../unlogged';

/**
 * Le Rapport : une checklist guidée pour boucler le mois, pas une page de chiffres
 * (docs/redesign-plus-douce.md, §Écran Rapport). L'écran dit une seule chose à la
 * fois — la prochaine étape — et replie tout le reste dessous : les autres factures,
 * puis les totaux, derrière un geste.
 *
 * La bande mois/trimestre paginée a disparu : on navigue aux flèches, et le mois se
 * choisit en tapant le titre (l'`<input type="month">` est posé dessus, invisible).
 */
export function Rapport() {
  const [search, setSearch] = useSearchParams();
  const requested = search.get('month');
  const month = requested && isValidMonth(requested) ? requested : previousMonth();
  const currentMonth = today().slice(0, 7);
  // Le mois qui se vit n'est pas « inachevé », il est en cours ; au-delà, il n'a
  // pas commencé (docs/redesign-mois-en-cours.md). Les flèches s'arrêtent au
  // présent — le futur appartient à Aujourd'hui et à la Semaine.
  const enCours = month >= currentMonth;

  const report = useResource(() => api.report.get(month), [month]);
  const notes = useResource(() => api.notes.list({ month }), [month]);
  const patients = useResource(() => api.patients.list(), []);
  const settings = useResource(() => api.settings.get(), []);
  const refreshNav = useRefreshNavCounts();

  function goToMonth(value: LocalMonth) {
    // Toujours explicite dans l'URL : un mois qu'elle a choisi ne doit pas être
    // repris par la règle d'ouverture (mois précédent bouclé → mois en cours).
    setSearch({ month: value }, { replace: true });
  }

  /** Pointer, émettre, envoyer : tout cela déplace aussi le point de la barre. */
  function onChanged() {
    report.reload();
    notes.reload();
    refreshNav();
  }

  const cloture = useMemo(
    () =>
      report.data && notes.data && patients.data
        ? planCloture(report.data, notes.data, patients.data)
        : null,
    [report.data, notes.data, patients.data],
  );

  // Les pétales tombent au passage du dernier état, une seule fois par mois — pas à
  // chaque ouverture de l'écran (§Animations). Jamais pour le mois en cours.
  useEffect(() => {
    if (enCours || cloture === null || cloture.steps === 0 || !cloture.allDone) return;
    petalsOnce(storageKey.moisBoucle(month));
  }, [cloture, month, enCours]);

  // Ouverture par défaut : le mois précédent tant que sa clôture attend, le mois en
  // cours dès qu'il est bouclé (ou qu'il n'a rien eu). Un lien profond n'est jamais
  // dérouté — seul le premier écran, sans ?month=, choisit pour elle. Et il ne choisit
  // qu'une fois : le mois qu'elle vient de clore ici reste à l'écran pour sa fête.
  const opening = useRef(true);
  useEffect(() => {
    if (!opening.current) return;
    if (requested !== null || month !== previousMonth()) {
      opening.current = false;
      return;
    }
    if (cloture === null) return;
    opening.current = false;
    if (cloture.steps === 0 || cloture.allDone) {
      setSearch({ month: currentMonth }, { replace: true });
    }
  }, [requested, month, cloture, currentMonth, setSearch]);

  // Le prénom vient de Réglages → Praticienne : la phrase s'adresse à elle, ou à
  // personne tant que le nom n'est pas renseigné.
  const firstName = firstNameOf(settings.data?.practitioner.name ?? '');
  // L'URSSAF n'apparaît que là où l'on déclare : le premier mois d'un trimestre
  // porte la déclaration du précédent (octobre parle de T3). Les autres mois n'ont
  // rien à dire — pas de pilule d'attente.
  const ownQuarter = quarterOf(month);
  const declarationQuarter =
    month === firstMonthOfQuarter(ownQuarter) ? previousQuarter(ownQuarter) : null;

  return (
    <section className="page report">
      <div className="report__screen">
        <header className="report__head">
          <div className="report__nav">
            <button
              type="button"
              className="report__arrow"
              aria-label="Mois précédent"
              onClick={() => goToMonth(addMonths(month, -1))}
            >
              ‹
            </button>
            <div className="report__month">
              <h1 className="report__title">{formatMonth(month)}</h1>
              <span className="report__quarter">
                Trimestre T{ownQuarter.slice(6, 7)} · {ownQuarter.slice(0, 4)} ·{' '}
                {[0, 1, 2]
                  .map((i) => formatMonthName(addMonths(firstMonthOfQuarter(ownQuarter), i)))
                  .join(', ')}
              </span>
              {/* Le sélecteur de mois est posé sur le titre, transparent : taper le
                  titre ouvre le calendrier, sans qu'une bande de pastilles occupe
                  le haut de l'écran. */}
              <input
                className="report__picker"
                type="month"
                value={month}
                max={currentMonth}
                aria-label="Choisir un mois"
                onChange={(event) => event.target.value && goToMonth(event.target.value)}
              />
            </div>
            <button
              type="button"
              className="report__arrow"
              aria-label="Mois suivant"
              disabled={month >= currentMonth}
              onClick={() => goToMonth(addMonths(month, 1))}
            >
              ›
            </button>
          </div>
          <p className="report__lead">
            {month > currentMonth
              ? `${capitalized(formatMonthLong(month))} n’a pas commencé.`
              : month === currentMonth
                ? `${capitalized(formatMonthLong(month))} suit son cours. Rien à boucler avant le 1er ${formatMonthLong(addMonths(month, 1))}.`
                : leadOf(cloture, month, firstName)}
          </p>
        </header>

        {report.error && (
          <p className="error" role="alert">
            {report.error}
          </p>
        )}
        {cloture === null && !report.error && <p className="muted">Chargement…</p>}

        {/* Le trimestre passe avant le mois : quand la fenêtre de déclaration est
            ouverte, c'est l'échéance qui commande. `key` remonte la carte d'un
            trimestre à l'autre — le relevé coché appartient à un seul d'entre eux. */}
        {declarationQuarter !== null && month <= currentMonth && (
          <Declaration
            key={declarationQuarter}
            quarter={declarationQuarter}
            onChanged={onChanged}
          />
        )}

        {enCours && month === currentMonth && (
          <Retour month={addMonths(month, -1)} patients={patients.data ?? null} onGo={goToMonth} />
        )}
        {enCours && <MoisEnCours month={month} onChanged={onChanged} />}

        {!enCours && cloture && <ClotureCard cloture={cloture} month={month} />}

        {!enCours && cloture && cloture.patients.length > 0 && (
          <Verification cloture={cloture} month={month} onChanged={onChanged} />
        )}

        {!enCours && report.data && <Chiffres report={report.data} month={month} />}
      </div>

      {/* Le récapitulatif papier, invisible à l'écran : la clôture est une checklist,
          l'impression reste le document par patient qu'elle donne et qu'elle garde. */}
      {report.data && <Printable report={report.data} notes={notes.data ?? []} month={month} />}
    </section>
  );
}

/**
 * La pilule de retour vers le mois précédent, sur le mois en cours seulement : ce
 * qu'août attend encore, ou la confirmation qu'il est bouclé. Sans séance en août,
 * rien — pas de porte vers un mois vide.
 */
function Retour({
  month,
  patients,
  onGo,
}: {
  month: LocalMonth;
  patients: Patient[] | null;
  onGo: (month: LocalMonth) => void;
}) {
  const report = useResource(() => api.report.get(month), [month]);
  const notes = useResource(() => api.notes.list({ month }), [month]);
  const cloture = useMemo(
    () =>
      report.data && notes.data && patients
        ? planCloture(report.data, notes.data, patients)
        : null,
    [report.data, notes.data, patients],
  );

  if (cloture === null || cloture.steps === 0) return null;
  const mois = capitalized(formatMonthLong(month));

  return (
    <button type="button" className="retour" onClick={() => onGo(month)}>
      {cloture.allDone ? (
        <span className="retour__texte retour__texte--fini">✓ {mois} est bouclé</span>
      ) : (
        <span className="retour__texte">
          <span className="retour__dot" aria-hidden="true" />
          {mois} attend encore{' '}
          {cloture.remaining === 1 ? 'une étape' : `${cloture.remaining} petites étapes`}
        </span>
      )}
      <span className="retour__chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/**
 * La carte de clôture : l'anneau qui avance, l'en-tête de la vérification dessous.
 * Bouclé, elle porte la fleur.
 */
function ClotureCard({ cloture, month }: { cloture: Cloture; month: LocalMonth }) {
  const mois = formatMonthLong(month);

  return (
    <section className="cloture" aria-label={`Clôture ${deMois(month)}`}>
      <div className="cloture__top">
        <Anneau done={cloture.done} steps={cloture.steps} allDone={cloture.allDone} />
        <div className="cloture__heading">
          <strong className="cloture__title">Clôture {deMois(month)}</strong>
          <span className="cloture__caption">{captionOf(cloture)}</span>
        </div>
      </div>

      {cloture.allDone && cloture.steps > 0 && (
        <div className="cloture__done">
          <span className="cloture__flower" aria-hidden="true">
            ❀
          </span>
          <strong className="cloture__done-title">{capitalized(mois)} est bouclé</strong>
          <p className="cloture__done-text">
            {cloture.patients.length > 1
              ? `Les ${cloture.patients.length} factures sont parties.`
              : 'La facture est partie.'}{' '}
            Il n’y a plus rien à faire ici avant la fin {deMois(addMonths(month, 1))}.
          </p>
        </div>
      )}

      {cloture.steps === 0 && <p className="cloture__empty">Aucune séance en {mois}.</p>}
    </section>
  );
}

/** L'anneau : les étapes faites sur les étapes du mois, et un cœur quand tout y est. */
function Anneau({ done, steps, allDone }: { done: number; steps: number; allDone: boolean }) {
  const circumference = 2 * Math.PI * 20;
  const ratio = steps === 0 ? 0 : done / steps;

  return (
    <svg
      className={`ring${allDone ? ' ring--done' : ''}`}
      width="54"
      height="54"
      viewBox="0 0 54 54"
      aria-hidden="true"
    >
      <circle className="ring__track" cx="27" cy="27" r="20" />
      <circle
        className="ring__arc"
        cx="27"
        cy="27"
        r="20"
        transform="rotate(-90 27 27)"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - ratio)}
      />
      <text className="ring__text" x="27" y="27" textAnchor="middle" dominantBaseline="central">
        {allDone ? '♥' : `${done}/${steps}`}
      </text>
    </svg>
  );
}

/**
 * Avant de clore : une ligne par famille, ce qu'elle a eu ce mois-ci et ce qui
 * cloche. Le bouton dessous fait tout le reste d'un geste — émettre ce qui manque,
 * envoyer ce qui peut partir — plutôt que trente taps famille par famille.
 */
function Verification({
  cloture,
  month,
  onChanged,
}: {
  cloture: Cloture;
  month: LocalMonth;
  onChanged: () => void;
}) {
  const email = useEmailConfigured();
  const verification = useMemo(
    () => verificationOf(cloture, { emailConfigured: email.configured }),
    [cloture, email.configured],
  );
  const [open, setOpen] = useState<number | null>(null);
  const [progress, setProgress] = useState<CloseProgress | null>(null);
  const [outcome, setOutcome] = useState<CloseOutcome | null>(null);
  const { toast, say } = useToast();
  const running = progress !== null;
  const mois = capitalized(formatMonthLong(month));

  async function clore(steps: CloseStep[], previous: CloseOutcome | null) {
    // Lus avant le passage : pendant, les rechargements font bouger la vérification.
    const { closesMonth } = verification;
    setOutcome(null);
    setProgress({ done: 0, total: steps.length, current: steps[0] ?? null });
    const result = await runClose(
      steps,
      {
        issue: (patientId) => api.notes.issue({ patientId, month }),
        send: (noteId) => api.notes.send(noteId),
      },
      (next) => {
        setProgress(next);
        // Chaque pas fait, la liste se recharge : les lignes changent sous ses yeux.
        if (next.done > 0) onChanged();
      },
    );
    const total = previous ? mergeOutcomes(previous, result) : result;
    setProgress(null);
    setOutcome(total);
    if (total.failures.length === 0 && closesMonth) {
      petalsOnce(storageKey.moisBoucle(month));
      say(bouclageToast(mois));
    }
  }

  return (
    <section className="ensuite verif">
      <h3 className="ensuite__title">
        {cloture.allDone ? `Les factures ${deMois(month)}` : 'À vérifier avant de clore'}
      </h3>
      <ul className="ensuite__list">
        {verification.rows.map((row) => (
          <Ligne
            key={row.patient.patientId}
            row={row}
            month={month}
            expanded={open === row.patient.patientId}
            onToggle={() =>
              setOpen(open === row.patient.patientId ? null : row.patient.patientId)
            }
            current={progress?.current?.patientId === row.patient.patientId}
            running={running}
            onChanged={onChanged}
          />
        ))}
      </ul>

      <div className="clore">
        {toast && <Toast>{toast}</Toast>}

        {progress && <Avancement progress={progress} />}

        {!running && outcome && (
          <div className="clore__bilan">
            <p className="clore__bilan-text">
              {outcomeSummary(outcome)}
            </p>
            {!verification.emailConfigured && (
              <p className="clore__hint">
                L’envoi par email n’est pas configuré (Réglages) : les factures émises
                attendent leur envoi.
              </p>
            )}
            {outcome.failures.length > 0 && (
              <div className="clore__echecs">
                {failureLines(outcome.failures).map((line) => (
                  <p className="clore__echec" key={line}>
                    {line}
                  </p>
                ))}
                <ul className="clore__details">
                  {outcome.failures.map((failure, index) => (
                    <li key={index}>
                      {failure.step.name} : {failure.message}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="button button--small clore__retry"
                  onClick={() => void clore(retryPlan(verification, outcome), outcome)}
                >
                  Réessayer
                </button>
              </div>
            )}
          </div>
        )}

        {!running && !outcome && !cloture.allDone && (
          <>
            <button
              type="button"
              className="declaration__cta clore__cta"
              disabled={
                verification.blocking > 0 || !email.resolved || verification.steps.length === 0
              }
              onClick={() => void clore(verification.steps, null)}
            >
              Clore le mois
            </button>
            <p className="clore__hint">
              {verification.blocking > 0 ? blockingReason(verification) : closeHint(verification)}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

/** La barre du passage : « 3 / 8 · Léa Marchand », et le remplissage qui suit. */
function Avancement({ progress }: { progress: CloseProgress }) {
  const { done, total, current } = progress;
  const ratio = total === 0 ? 1 : done / total;

  return (
    <div
      className="clore__progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-label="Clôture du mois"
    >
      <div className="clore__bar">
        <span className="clore__fill" style={{ width: `${ratio * 100}%` }} />
      </div>
      <p className="clore__step">
        {Math.min(done + 1, total)} / {total}
        {current ? ` · ${current.name}` : ''}
      </p>
    </div>
  );
}

/**
 * Une famille : son nom (vers sa fiche), ce qu'elle a eu, et ce qui cloche dessous.
 * Taper la ligne déplie ses séances et les gestes à la main, pour les exceptions.
 */
function Ligne({
  row,
  month,
  expanded,
  onToggle,
  current,
  running,
  onChanged,
}: {
  row: VerificationRow;
  month: LocalMonth;
  expanded: boolean;
  onToggle: () => void;
  current: boolean;
  running: boolean;
  onChanged: () => void;
}) {
  const { patient, notices } = row;
  const seance = patient.unlogged[0];

  return (
    <li className={`ensuite__item verif__item${current ? ' is-current' : ''}`}>
      {/* Toute la ligne déplie ; le nom, lui, mène à la fiche. Le chevron reste le
          vrai bouton, celui que le clavier et le lecteur d'écran trouvent. */}
      <div
        className="verif__head"
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('a, button')) onToggle();
        }}
      >
        <span className="verif__label">
          <Link className="verif__name" to={`/patients/${patient.patientId}`}>
            {patient.name}
          </Link>
          <span className="verif__summary">{summaryLine(patient.group)}</span>
        </span>
        <button
          type="button"
          className="verif__chevron"
          aria-expanded={expanded}
          aria-label={expanded ? 'Masquer les séances' : 'Voir les séances'}
          onClick={onToggle}
        >
          {expanded ? '⌃' : '⌄'}
        </button>
      </div>

      {notices.length > 0 && (
        <ul className="verif__notices">
          {notices.map((notice) => (
            <li className={`verif__notice verif__notice--${notice.kind}`} key={notice.text}>
              {notice.text}
              {notice.editPatient && (
                <>
                  {' '}
                  <Link className="verif__edit" to={`/patients/${patient.patientId}/modifier`}>
                    Modifier le patient
                  </Link>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* La séance à pointer se pointe ici même : c'est elle qui retient le bouton. */}
      {seance && (
        <div className="verif__seance">
          <span className="verif__seance-label">
            Séance du {formatShortDate(dateOf(seance.startsAt))} · {timeOf(seance.startsAt)} ·{' '}
            {seance.locationLabel}
          </span>
          <OutcomeButtons key={seance.id} appointment={seance} onLogged={onChanged} />
        </div>
      )}

      {expanded && (
        <Panneau patient={patient} month={month} running={running} onChanged={onChanged} />
      )}
    </li>
  );
}

/** Le détail déplié : les séances, le PDF, et les gestes à la main pour une exception. */
function Panneau({
  patient,
  month,
  running,
  onChanged,
}: {
  patient: PatientCloture;
  month: LocalMonth;
  running: boolean;
  onChanged: () => void;
}) {
  const email = useEmailConfigured();
  const { toast, say } = useToast();
  const actions = useActions(say, onChanged);
  const unsent = patient.unsentNote;
  const lastSent = patient.notes.find((note) => note.sentTo !== null) ?? null;
  const busy = actions.pending || running;
  const noEmail = email.configured ? undefined : 'L’envoi par email n’est pas configuré (Réglages).';

  return (
    <div className="ensuite__panel">
      {toast && <Toast>{toast}</Toast>}
      <ul className="ensuite__seances">
        {patient.group.entries.map((entry) => (
          <li className="ensuite__seance" key={entry.id}>
            <span>
              {formatShortDate(dateOf(entry.startsAt))} · {timeOf(entry.startsAt)}
            </span>
            <span className="ensuite__place">{entry.locationLabel}</span>
            <span className="ensuite__outcome">{outcomeOf(entry)}</span>
          </li>
        ))}
      </ul>

      <div className="ensuite__actions">
        {patient.lastNote && (
          <a
            className="button button--small"
            href={api.notes.pdfUrl(patient.lastNote.id)}
            target="_blank"
            rel="noopener"
          >
            Voir le PDF
          </a>
        )}

        {/* Rien ne s'émet tant qu'une séance attend — la facture serait incomplète — ni
            sans email : elle n'aurait nulle part où aller. */}
        {patient.needsIssue && patient.unlogged.length === 0 && patient.canEmail && (
          <button
            type="button"
            className="button button--primary button--small"
            disabled={busy}
            onClick={(event) =>
              actions.run(event, undefined, `Facture de ${patient.firstName} émise`, () =>
                api.notes.issue({ patientId: patient.patientId, month }),
              )
            }
          >
            Émettre la facture
          </button>
        )}

        {patient.canEmail && unsent && (
          <button
            type="button"
            className="button button--primary button--small"
            disabled={busy || !email.configured}
            title={noEmail}
            onClick={(event) =>
              actions.run(event, undefined, sentMessage(patient), () => api.notes.send(unsent.id))
            }
          >
            {sendLabel(patient)}
          </button>
        )}

        {patient.canEmail && !unsent && lastSent && (
          <button
            type="button"
            className="button button--small"
            disabled={busy || !email.configured}
            title={noEmail}
            onClick={(event) =>
              actions.run(event, undefined, sentMessage(patient), () =>
                api.notes.send(lastSent.id),
              )
            }
          >
            Renvoyer
          </button>
        )}
      </div>

      {actions.error && (
        <p className="error" role="alert">
          {actions.error}
        </p>
      )}
    </div>
  );
}

/** Les chiffres du mois, derrière un geste : ils ne se lisent pas avant les gestes. */
function Chiffres({ report, month }: { report: ReportResponse; month: LocalMonth }) {
  const [open, setOpen] = useState(false);
  const mois = deMois(month);

  return (
    <section className="chiffres">
      <button
        type="button"
        className="chiffres__toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>
          {open ? 'Masquer' : 'Voir'} les chiffres {mois}
        </span>
        <span className="chiffres__chevron" aria-hidden="true">
          {open ? '⌃' : '⌄'}
        </span>
      </button>

      {open && (
        <div className="chiffres__grid">
          <p className="chiffres__card">
            <span className="chiffres__label">Facturé en {formatMonthLong(month)}</span>
            <strong className="chiffres__value">{formatEuros(report.factureEur)}</strong>
          </p>
          <p className="chiffres__card">
            <span className="chiffres__label">Encaissé en {formatMonthLong(month)}</span>
            <strong className="chiffres__value">{formatEuros(report.encaisseEur)}</strong>
          </p>
          {report.retrocession && <Retrocession retrocession={report.retrocession} />}
          <button type="button" className="chiffres__print" onClick={() => window.print()}>
            Imprimer le récapitulatif
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Ce que le mois doit au cabinet : une ligne par famille adressée, avec ce qu'elle a
 * réglé, et la somme à reverser. Il se pose sous les chiffres du mois sans les
 * toucher — ce qu'elle a facturé et encaissé reste ce qu'il était.
 */
function Retrocession({ retrocession }: { retrocession: RetrocessionMonth }) {
  return (
    <section className="retro">
      <h3 className="retro__title">Rétrocession cabinet · {formatPercent(retrocession.ratePct)}</h3>
      <ul className="retro__list">
        {retrocession.lines.map((line) => (
          <li className="retro__line" key={line.patientId}>
            <span className="retro__name">{line.patientName}</span>
            <span className="retro__amount">{formatEuros(line.encaisseEur)}</span>
          </li>
        ))}
      </ul>
      <p className="retro__total">
        <span>À reverser</span>
        <strong>{formatEuros(retrocession.totalEur)}</strong>
      </p>
    </section>
  );
}

/**
 * Le récapitulatif papier : le même document qu'avant le redesign, séance par séance
 * et patient par patient. Il ne s'affiche jamais à l'écran — l'écran, lui, guide ;
 * le papier, lui, se classe et se remet.
 */
function Printable({
  report,
  notes,
  month,
}: {
  report: ReportResponse;
  notes: Note[];
  month: LocalMonth;
}) {
  return (
    <div className="report__print">
      <h1 className="report__print-title">Récapitulatif — {formatMonth(month)}</h1>

      <section className="report__totals" aria-label="Le mois">
        <p className="report__total">
          <span className="report__total-label">Facturé</span>
          <span className="report__total-value">{formatEuros(report.factureEur)}</span>
        </p>
        <p className="report__total">
          <span className="report__total-label">Encaissé</span>
          <span className="report__total-value">{formatEuros(report.encaisseEur)}</span>
        </p>
      </section>

      {report.patients.map((group) => (
        <section className="report__patient" key={group.patientId}>
          <h2 className="report__name">{group.patientName}</h2>
          <ul className="report__seances">
            {group.entries.map((entry) => (
              <li
                className={`report__seance${entry.status === 'planned' ? ' is-unlogged' : ''}`}
                key={entry.id}
              >
                <span className="report__date">{formatDate(dateOf(entry.startsAt))}</span>
                <span className="report__time">{timeOf(entry.startsAt)}</span>
                <span className="report__place">{entry.locationLabel}</span>
                <span className="report__outcome">{outcomeOf(entry)}</span>
                {entry.amountEur !== null && (
                  <span className="report__amount">{formatEuros(entry.amountEur)}</span>
                )}
                {entry.noteNumber && <span className="report__note-ref">{entry.noteNumber}</span>}
              </li>
            ))}
          </ul>
          <div className="report__summary">
            <p className="report__summary-line">{summaryOf(group)}</p>
            {group.doneCount > 0 && (
              <p className="report__summary-line">Total : {formatEuros(group.totalEur)}</p>
            )}
            {notes
              .filter((note) => note.patientId === group.patientId)
              .map((note) => (
                <p className="report__summary-line" key={note.id}>
                  {note.number} — émise le {formatDate(note.issuedOn)} ·{' '}
                  {formatEuros(note.totalEur)}
                </p>
              ))}
          </div>
        </section>
      ))}

      <p className="report__rate">Taux horaire : {formatEuros(report.hourlyRateEur)}/h</p>
    </div>
  );
}

/**
 * Un geste, une récompense : les cœurs partent du doigt, le toast dit ce qui vient
 * d'être fait, et l'écran se recharge. Le même chemin pour les trois actions — elles
 * ne diffèrent que par la phrase et la couleur.
 */
function useActions(say: (text: string) => void, onChanged: () => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run<T>(
    event: MouseEvent,
    palette: readonly string[] | undefined,
    message: string | ((result: T) => string),
    action: () => Promise<T>,
  ) {
    setPending(true);
    setError(null);
    try {
      const result = await action();
      hearts(event.clientX, event.clientY, palette);
      say(typeof message === 'function' ? message(result) : message);
      onChanged();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return { pending, error, run };
}

function sendLabel(patient: PatientCloture): string {
  return patient.recipients > 1 ? `Envoyer à ${patient.recipients} adresses` : 'Envoyer par email';
}

/** Un envoi à plusieurs adresses peut n'atteindre qu'une partie : le toast le dit. */
function sentMessage(patient: PatientCloture): (result: SendNoteResponse) => string {
  return ({ note, failed }) => {
    if (failed.length === 0) return `Facture de ${patient.firstName} envoyée`;
    const reached = parseEmails(note.sentTo).length;
    const total = reached + failed.length;
    const echec = formatEmails(failed);
    return `Envoyée à ${reached} adresse${plural(reached)} sur ${total} — échec : ${echec}`;
  };
}

/** La phrase d'accroche : ce qu'il reste, ou le repos mérité. */
function leadOf(cloture: Cloture | null, month: LocalMonth, firstName: string): string {
  const mois = formatMonthLong(month);
  if (cloture === null) return '';
  if (cloture.steps === 0) return `Aucune séance en ${mois}.`;
  if (cloture.allDone) {
    return firstName
      ? `Tout est en ordre pour ${mois}, ${firstName}.`
      : `Tout est en ordre pour ${mois}.`;
  }
  if (cloture.remaining === 1) return `Plus qu’une étape et ${mois} est bouclé.`;
  return `Encore ${cloture.remaining} petites étapes et ${mois} est bouclé.`;
}

/** La légende de l'anneau : des étapes tant qu'il en reste, des factures une fois fini. */
function captionOf(cloture: Cloture): string {
  if (cloture.steps === 0) return 'Rien à boucler';
  if (cloture.allDone) {
    return `${cloture.patients.length} facture${plural(cloture.patients.length)} envoyée${plural(
      cloture.patients.length,
    )} · tout est parti`;
  }
  return `${cloture.done} étapes sur ${cloture.steps} · ${cloture.sentNotes} facture${plural(
    cloture.sentNotes,
  )} partie${plural(cloture.sentNotes)}, ${cloture.pendingNotes} à finir`;
}

/** A `done` séance shows its billable duration; the others show why it did not happen. */
function outcomeOf(entry: ReportEntry): string {
  if (entry.status === 'done') return doneLabel(entry.actualDurationMin, entry.free);
  if (entry.status === 'planned') return 'à pointer';
  return STATUS_LABELS[entry.status];
}

/** « 4 séances · 3 h · 150,00 € » : de quoi vérifier d'un coup d'œil. Les offertes comptent en heures. */
function summaryLine(group: ReportPatientGroup): string {
  if (group.doneCount === 0) {
    const count = group.entries.length;
    return `${count} séance${plural(count)} · aucune effectuée`;
  }
  return `${group.doneCount} séance${plural(group.doneCount)} · ${formatDuration(
    group.totalMinutes,
  )} · ${formatEuros(group.totalEur)}`;
}

function summaryOf(group: ReportPatientGroup): string {
  const parts = [
    `${group.doneCount} séance${plural(group.doneCount)} effectuée${plural(group.doneCount)}`,
  ];
  if (group.doneCount > 0) parts.push(`${formatDuration(group.totalMinutes)} au total`);
  if (group.noShowCount > 0) parts.push(`${group.noShowCount} pas venu·e`);
  if (group.cancelledCount > 0) {
    parts.push(`${group.cancelledCount} annulée${plural(group.cancelledCount)}`);
  }
  if (group.unloggedCount > 0) {
    parts.push(`${group.unloggedCount} non pointée${plural(group.unloggedCount)}`);
  }
  return parts.join(' · ');
}

function plural(count: number): string {
  return count > 1 ? 's' : '';
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
